package org.moxultimate.topwithmox.product.ui

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.product.api.ProductRepository
import org.moxultimate.topwithmox.product.data.ProductContentResponse

/**
 * ProductViewModel
 * Manages cursor-based pagination and client-side type/search filtering for product content.
 * Filters apply over the already-loaded [accumulatedContents] without triggering a new API call.
 *
 * @param repository Data source for product content and types.
 */
class ProductViewModel(
    private val repository: ProductRepository,
) : ViewModel() {

    private val _screenState = MutableStateFlow<ProductState>(ProductState.Loading)
    val screenState: StateFlow<ProductState> = _screenState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMore = MutableStateFlow(true)
    val hasMore: StateFlow<Boolean> = _hasMore

    private val _allTypes = MutableStateFlow<List<String>>(emptyList())
    val allTypes: StateFlow<List<String>> = _allTypes

    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    private val accumulatedContents = mutableListOf<ProductContentResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMoreItems = true

    init {
        loadProductContents()
        loadAllTypes()
        loadAllTags()
    }

    /**
     * loadProductContents
     * Resets accumulated state and fetches the first page of product content.
     */
    fun loadProductContents() {
        accumulatedContents.clear()
        lastId = Long.MAX_VALUE
        hasMoreItems = true
        _hasMore.value = true
        fetchPage(isInitialLoad = true)
    }

    /**
     * loadMoreIfNeeded
     * Called by the scroll handler when the user approaches the bottom.
     * No-op when already loading or no more pages exist.
     */
    fun loadMoreIfNeeded() {
        if (hasMoreItems && !_isLoadingMore.value) fetchPage(isInitialLoad = false)
    }

    /**
     * toggleType
     * Adds or removes [type] from the active type chip filter set and rebuilds the screen state.
     *
     * @param type Type string matching a value from [allTypes].
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        _screenState.value = buildState()
    }

    /**
     * setSearchText
     * Updates the live search text and immediately re-filters the loaded content list.
     * No API call is made — pure client-side filter on content title and description.
     *
     * @param text Text to search for in content title and description.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        _screenState.value = buildState()
    }

    /**
     * clearAllFilters
     * Resets all active type chips and search text, restoring the full content list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _searchText.value = ""
        _screenState.value = buildState()
    }

    private fun fetchPage(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getProductContents(lastId).collect { state ->
                _screenState.value = when (state) {
                    is APIState.Loading -> {
                        if (isInitialLoad) ProductState.Loading else _screenState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        ProductState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        _isLoadingMore.value = false
                        val page = state.data
                        if (page.size < 20) hasMoreItems = false
                        accumulatedContents.addAll(page)
                        if (page.isNotEmpty()) lastId = page.last().id
                        _hasMore.value = hasMoreItems
                        buildState()
                    }
                }
            }
        }
    }

    private fun loadAllTypes() {
        viewModelScope.launch {
            repository.getAllTypes().collect { state ->
                if (state is APIState.Success) _allTypes.value = state.data.distinct().sorted()
            }
        }
    }

    private fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    /**
     * buildState
     * Applies active type chips and search text as client-side filters over [accumulatedContents].
     * Type filter: content type must match any selected type (OR logic, case- and whitespace-insensitive).
     * Search filter: content title, description, or tags must contain the search text (case-insensitive).
     */
    private fun buildState(): ProductState.Success {
        val types = _selectedTypes.value
        val text = _searchText.value.trim()

        val result = accumulatedContents.filter { content ->
            val matchesType = types.isEmpty() ||
                    types.any { type -> content.tags?.contains(type.trim(), ignoreCase = true) == true }
            val matchesText = text.isBlank() ||
                    content.title.contains(text, ignoreCase = true) ||
                    content.description?.contains(text, ignoreCase = true) == true ||
                    content.tags?.contains(text, ignoreCase = true) == true
            matchesType && matchesText
        }

        return ProductState.Success(
            contentList = result,
            hasMore = hasMoreItems,
        )
    }
}
